import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { confirmPayment, completePayment, initiateMpesaPayment, checkMpesaStatus } from "../../../ReduxSlices/Bills/billSlice";
import { selectMpesaStatus, selectMpesaLoading, selectCheckoutRequestId } from "../../../ReduxSlices/Bills/billSlice";

const PaymentModal = ({ previewData, onClose, onPaymentSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [bill, setBill] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [phoneNumber, setPhoneNumber] = useState("");
  const dispatch = useDispatch();
  const mpesaStatus = useSelector(selectMpesaStatus);
  const mpesaLoading = useSelector(selectMpesaLoading);
  const checkoutRequestId = useSelector(selectCheckoutRequestId);
  const pollRef = useRef(null);

  useEffect(() => {
    if (previewData?.patient?.contact_info) {
      const contact = previewData.patient.contact_info;
      const cleaned = contact.replace(/[^0-9]/g, "");
      if (cleaned.startsWith("254") || cleaned.startsWith("07") || cleaned.startsWith("01") || cleaned.startsWith("0")) {
        setPhoneNumber(contact);
      }
    }
  }, [previewData]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    if (mpesaStatus === "completed" && checkoutRequestId) {
      if (pollRef.current) clearInterval(pollRef.current);
      finishMpesaPayment();
    }
    if (mpesaStatus === "failed" && checkoutRequestId) {
      if (pollRef.current) clearInterval(pollRef.current);
      setLoading(false);
    }
  }, [mpesaStatus, checkoutRequestId]);

  const startPolling = (checkoutId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      dispatch(checkMpesaStatus(checkoutId));
    }, 3000);
  };

  const handleCashPayment = async () => {
    try {
      setLoading(true);
      const patientId = previewData.patient?.id;
      const category = previewData.category;
      const serviceId = previewData.service_id;
      const patientQueueId = previewData.patient_queue_id;

      if (!patientId || !category) {
        alert("Missing patient ID or category.");
        return;
      }

      const billResponse = await dispatch(
        confirmPayment({ patientId, category, serviceId, patientQueueId, paymentMethod: "cash" })
      ).unwrap();

      const newBillId = billResponse.id;
      setBill(billResponse);

      await dispatch(completePayment(newBillId)).unwrap();

      if (onPaymentSuccess) onPaymentSuccess(newBillId);
      onClose();
    } catch (err) {
      console.error("Payment confirmation failed:", err);
      alert("Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMpesaPayment = async () => {
    try {
      setLoading(true);
      const patientId = previewData.patient?.id;
      const category = previewData.category;
      const serviceId = previewData.service_id;
      const patientQueueId = previewData.patient_queue_id;

      if (!patientId || !category) {
        alert("Missing patient ID or category.");
        return;
      }

      if (!phoneNumber || phoneNumber.trim() === "") {
        alert("Please enter the patient's phone number.");
        setLoading(false);
        return;
      }

      const billResponse = await dispatch(
        confirmPayment({ patientId, category, serviceId, patientQueueId, paymentMethod: "mpesa" })
      ).unwrap();

      const newBillId = billResponse.id;
      setBill(billResponse);

      const result = await dispatch(initiateMpesaPayment({ billId: newBillId, phoneNumber })).unwrap();

      if (result.success) {
        startPolling(result.checkout_request_id);
      } else {
        alert(result.error || "M-Pesa initiation failed");
        setLoading(false);
      }
    } catch (err) {
      console.error("M-Pesa payment failed:", err);
      alert("M-Pesa payment failed. Please try again.");
      setLoading(false);
    }
  };

  const finishMpesaPayment = async () => {
    try {
      if (!bill) return;
      await dispatch(completePayment(bill.id)).unwrap();
      if (onPaymentSuccess) onPaymentSuccess(bill.id);
      onClose();
    } catch (err) {
      console.error("Finalizing M-Pesa payment failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = () => {
    if (paymentMethod === "cash") {
      handleCashPayment();
    } else {
      handleMpesaPayment();
    }
  };

  const handleCancel = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    onClose();
  };

  const items =
    bill?.bill_items ||
    previewData?.bill_items ||
    previewData?.preview ||
    [];
  const total =
    bill?.total_amount ||
    previewData?.total_amount ||
    previewData?.total_estimate ||
    items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);

  const isWaitingForMpesa = mpesaStatus === "sent" || mpesaStatus === "pending";
  const isMpesaCompleted = mpesaStatus === "completed";
  const isMpesaFailed = mpesaStatus === "failed";
  const isMpesaSending = mpesaStatus === "sending";

  return (
    <div className="modal-overlay">
      <div className="bill-modal bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h2 className="text-lg font-bold mb-4">
          {bill ? "Bill Summary" : "Bill Preview"}
        </h2>

        {items.length > 0 ? (
          <ul className="mb-4">
            {items.map((item, idx) => (
              <li key={item.id || idx} className="flex justify-between">
                <span>{item.description}</span>
                <span>
                  Ksh{(parseFloat(item.unit_price) * item.quantity).toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p>No items available.</p>
        )}

        <div className="flex justify-between font-semibold mb-4">
          <span>{bill ? "Total:" : "Total Estimate:"}</span>
          <span>Ksh{typeof total === 'number' ? total.toFixed(2) : parseFloat(total).toFixed(2)}</span>
        </div>

        {!bill && !isWaitingForMpesa && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Payment Method</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === "cash"}
                  onChange={() => setPaymentMethod("cash")}
                />
                <span>Cash</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="mpesa"
                  checked={paymentMethod === "mpesa"}
                  onChange={() => setPaymentMethod("mpesa")}
                />
                <span>M-Pesa</span>
              </label>
            </div>
          </div>
        )}

        {!bill && paymentMethod === "mpesa" && !isWaitingForMpesa && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Patient Phone Number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g. 254712345678"
              className="w-full border rounded px-3 py-2"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">Enter the M-Pesa registered phone number</p>
          </div>
        )}

        {isWaitingForMpesa && (
          <div className="mb-4 p-4 bg-blue-50 rounded text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-blue-700 font-medium">Check your phone for M-Pesa prompt</p>
            <p className="text-sm text-blue-500 mt-1">Enter your PIN to complete payment</p>
            <p className="text-xs text-gray-400 mt-2">Waiting for confirmation...</p>
          </div>
        )}

        {isMpesaFailed && (
          <div className="mb-4 p-4 bg-red-50 rounded text-center">
            <p className="text-red-700 font-medium">M-Pesa payment failed</p>
            <p className="text-sm text-red-500 mt-1">Please try again or use a different method</p>
          </div>
        )}

        {isMpesaCompleted && (
          <div className="mb-4 p-4 bg-green-50 rounded text-center">
            <p className="text-green-700 font-medium">Payment received!</p>
            <p className="text-sm text-green-500 mt-1">Finalizing...</p>
          </div>
        )}

        <div className="flex justify-end gap-[10px] mt-[12px]">
          <button
            onClick={handleCancel}
            type="button"
            className="secondary"
            disabled={loading && !isWaitingForMpesa}
          >
            {isWaitingForMpesa ? "Cancel Payment" : "Close"}
          </button>
          {!bill && !isWaitingForMpesa && (
            <button
              onClick={handleConfirmPayment}
              type="button"
              className="primary"
              disabled={loading || isMpesaSending}
            >
              {loading ? "Processing..." : paymentMethod === "cash" ? "Confirm & Pay Cash" : "Pay via M-Pesa"}
            </button>
          )}
          {isMpesaFailed && !bill && (
            <button
              onClick={handleMpesaPayment}
              type="button"
              className="primary"
              disabled={loading}
            >
              Retry M-Pesa
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
